// src/pages/admin/roles/Users.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AddUserModal from '../../../components/modals/AddUserModal'
import EditUserModal from '../../../components/modals/EditUserModal'
import ResetPasswordModal from '../../../components/modals/ResetPasswordModal'
import DeleteConfirmationModal from '../../../components/modals/DeleteConfirmationModal'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MoreVertical,
  Clock,
  Key,
  Users as UsersIcon,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Shield,
  User,
  Building,
  ChevronDown,
  Filter
} from 'lucide-react'
import UserService from '../../../services/userService'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import BranchService from '../../../services/branchService'
import { usePermissions } from '../../../hooks/usePermissions'
import { PERMISSIONS } from '../../../config/roles'
import AuthService from '../../../services/authService'

export const Users = () => {
  // Get current user and permissions
  const currentUser = AuthService.getCurrentUser()
  const permissions = usePermissions(currentUser)
  const hasLoadedRef = useRef(false)
  
  const [users, setUsers] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [actionMenuOpen, setActionMenuOpen] = useState(null)
  const [toast, setToast] = useState(null)
  const [filters, setFilters] = useState({
    role: '',
    branch: '',
    status: ''
  })

  // Helper function to safely get property value
  const safeGet = useCallback((obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }, [])

  // Helper function to validate user object
  const validateUser = useCallback((user) => {
    if (!user || typeof user !== 'object') return false
    return user._id && user.username
  }, [])

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Simple fetch functions without complex dependencies
  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Log manager access to users data
      if (permissions.logBranchAccess) {
        permissions.logBranchAccess('users', 'view')
      }
      
      // Check if user is branch-restricted
      const isRestricted = permissions.isBranchRestricted
      const userBranchId = currentUser?.branchId
      
      // Apply branch filtering for restricted users
      let params = {}
      if (isRestricted && userBranchId) {
        params.branchId = userBranchId
      }
      
      const response = await UserService.getAllUsers(params)

      if (response && response.success) {
        const usersData = safeGet(response, 'data.users', [])
        // Filter out invalid users
        let validUsers = Array.isArray(usersData)
          ? usersData.filter(validateUser)
          : []

        // Additional client-side filtering for branch-restricted users
        if (isRestricted && userBranchId) {
          validUsers = validUsers.filter(user => 
            user.branchId === userBranchId
          )
        }

        setUsers(validUsers)
        console.log('👥 Users loaded:', validUsers.length, isRestricted ? '(filtered by branch)' : '')
      } else {
        setError(safeGet(response, 'message', 'Failed to fetch users'))
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      setError(error?.message || 'Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const loadBranches = async () => {
    try {
      setBranchesLoading(true)
      console.log('🏢 Fetching branches for user management...')
      const response = await BranchService.getAllBranches()

      if (response && response.success) {
        const branchesData = response.data?.branches || []
        console.log('🏢 Branches fetched:', branchesData.length)
        setBranches(branchesData)
      } else {
        console.warn('⚠️ Failed to fetch branches:', response)
        setBranches([])
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
      setBranches([])
    } finally {
      setBranchesLoading(false)
    }
  }

  // Check if current user can perform various actions
  const canCreateUsers = permissions.hasPermission(PERMISSIONS.USERS.CREATE)
  const canEditUsers = permissions.hasPermission(PERMISSIONS.USERS.EDIT)
  const canDeleteUsers = permissions.hasPermission(PERMISSIONS.USERS.DELETE)
  const canResetPasswords = permissions.hasPermission(PERMISSIONS.USERS.RESET_PASSWORD)
  const canViewUsers = permissions.hasPermission(PERMISSIONS.USERS.VIEW)

  // Load initial data - only run once when component mounts and user has permissions
  useEffect(() => {
    if (canViewUsers && !hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadBranches()
      loadUsers()
    }
  }, [canViewUsers]) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh function
  const handleRefresh = () => {
    loadBranches()
    loadUsers()
  }

  // If user doesn't have permission to view users, show access denied
  if (!canViewUsers) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Shield className="h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">You don't have permission to view users.</p>
      </div>
    )
  }

  // Get branch name by ID
  const getBranchName = (branchIdOrObject) => {
    // If it's already an object with name, return the name  
    if (branchIdOrObject && typeof branchIdOrObject === 'object' && branchIdOrObject.name) {
      return branchIdOrObject.name
    }

    // If it's a string ID, find the branch in our branches array
    if (typeof branchIdOrObject === 'string') {
      const branch = branches.find(b => b._id === branchIdOrObject)
      return branch?.name || 'Unknown Branch'
    }

    return 'No Branch'
  }

  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      super_admin: 'bg-purple-100 text-purple-800',
      manager: 'bg-blue-100 text-blue-800',
      cashier: 'bg-green-100 text-green-800',
      barista: 'bg-yellow-100 text-yellow-800'
    }
    return colors[role?.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  // Get status badge color
  const getStatusBadgeColor = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
      case 'super_admin':
        return <Shield className="w-3 h-3" />
      case 'manager':
        return <Building className="w-3 h-3" />
      default:
        return <User className="w-3 h-3" />
    }
  }

  // Calculate statistics
  const getStatistics = () => {
    const totalUsers = users.length
    const activeUsers = users.filter(user => safeGet(user, 'isActive')).length
    const inactiveUsers = totalUsers - activeUsers
    const roles = [...new Set(users.map(user => safeGet(user, 'role')))].filter(Boolean).length

    return {
      total: totalUsers,
      active: activeUsers,
      inactive: inactiveUsers,
      roles: roles || 1
    }
  }

  // Handle user added
  const handleUserAdded = (newUser) => {
    permissions.logBranchAccess('users', 'create')
    if (validateUser(newUser)) {
      setUsers(prev => [newUser, ...prev])
      showToast('User created successfully!', 'success')
    }
  }

  // Handle opening add user modal
  const handleAddUserClick = () => {
    permissions.logBranchAccess('users', 'create_form')
    setShowAddModal(true)
  }

  // Handle user updated
  const handleUserUpdated = (updatedUser) => {
    if (validateUser(updatedUser)) {
      setUsers(prev => prev.map(user =>
        safeGet(user, '_id') === safeGet(updatedUser, '_id')
          ? { ...user, ...updatedUser }
          : user
      ))
      showToast('User updated successfully!', 'success')
    }
  }

  // Handle user deleted
  const handleUserDeleted = (userId) => {
    if (userId) {
      setUsers(prev => prev.filter(user => safeGet(user, '_id') !== userId))
      showToast('User deleted successfully', 'success')
    }
  }

  // Handle password reset
  const handlePasswordReset = () => {
    showToast('Password reset successfully', 'success')
  }

  // Handle edit button click
  const handleEditClick = (user) => {
    permissions.logBranchAccess('users', 'edit')
    setSelectedUser(user)
    setShowEditModal(true)
    setActionMenuOpen(null)
  }

  // Handle reset password button click
  const handleResetPasswordClick = (user) => {
    permissions.logBranchAccess('users', 'reset_password')
    setSelectedUser(user)
    setShowResetPasswordModal(true)
    setActionMenuOpen(null)
  }

  // Handle delete button click
  const handleDeleteClick = (user) => {
    permissions.logBranchAccess('users', 'delete')
    setSelectedUser(user)
    setShowDeleteModal(true)
    setActionMenuOpen(null)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setFilters({
      role: '',
      branch: '',
      status: ''
    })
  }

  // Filter users based on search term and filters
  const filteredUsers = users.filter(user => {
    if (!validateUser(user)) return false

    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = !searchTerm ||
      safeGet(user, 'username', '').toLowerCase().includes(searchLower) ||
      safeGet(user, 'role', '').toLowerCase().includes(searchLower) ||
      getBranchName(safeGet(user, 'branchId')).toLowerCase().includes(searchLower)

    const matchesRole = !filters.role || safeGet(user, 'role') === filters.role
    const matchesBranch = !filters.branch || safeGet(user, 'branchId') === filters.branch
    const matchesStatus = !filters.status ||
      (filters.status === 'active' && safeGet(user, 'isActive')) ||
      (filters.status === 'inactive' && !safeGet(user, 'isActive'))

    return matchesSearch && matchesRole && matchesBranch && matchesStatus
  })

  const statistics = getStatistics()

  return (
    <div className="p-6 space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 max-w-md w-full ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white p-4 rounded-lg shadow-lg`}>
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || branchesLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${(loading || branchesLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canCreateUsers && (
            <Button
              onClick={handleAddUserClick}
              className="bg-black hover:bg-gray-800 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UsersIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.inactive}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Roles</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.roles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter</h3>
            {(searchTerm || filters.role || filters.branch || filters.status) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search user by name, role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={filters.role}
                onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
                <option value="manager">Manager</option>
                <option value="cashier">Cashier</option>
                <option value="barista">Barista</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Branch Filter */}
            <div className="relative">
              <select
                value={filters.branch}
                onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                disabled={branchesLoading}
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardContent className="p-0">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Users List ({filteredUsers.length})
              </h3>
            </div>
          </div>
          {/* Loading State */}
          {loading && (
            <InlineLoader text="Loading users..." />
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading users</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={loadUsers}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {users.length === 0 ? 'No users found' : 'No users match your filters'}
              </h3>
              <p className="text-gray-500 mb-4">
                {users.length === 0
                  ? 'Get started by creating your first user.'
                  : 'Try adjusting your search criteria.'
                }
              </p>
              {users.length === 0 && (
                <Button
                  onClick={handleAddUserClick}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              )}
            </div>
          )}

          {/* Users Table */}
          {!loading && !error && filteredUsers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manage Password</th>
                    <th className="pl-6 pr-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={safeGet(user, '_id')} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {safeGet(user, 'username')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(safeGet(user, 'role'))}`}>
                          {getRoleIcon(safeGet(user, 'role'))}
                          {safeGet(user, 'role', '').replace('_', ' ').charAt(0).toUpperCase() + safeGet(user, 'role', '').replace('_', ' ').slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span>{getBranchName(safeGet(user, 'branchId'))}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(safeGet(user, 'isActive'))}`}>
                          {safeGet(user, 'isActive') ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {formatDate(safeGet(user, 'lastLogin'))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {canResetPasswords ? (
                          <button
                            onClick={() => handleResetPasswordClick(user)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm underline hover:no-underline transition-colors"
                          >
                            <Key className="w-3 h-3" />
                            reset password
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {/* Status Toggle Button */}
                          {/* <button
                            onClick={() => handleToggleStatus(
                              safeGet(user, '_id'),
                              safeGet(user, 'isActive'),
                              safeGet(user, 'username')
                            )}
                            disabled={actionLoading[safeGet(user, '_id')] === 'toggling'}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                            title={safeGet(user, 'isActive') ? 'Deactivate' : 'Activate'}
                          >
                            {actionLoading[safeGet(user, '_id')] === 'toggling' ? (
                              <ButtonSpinner />
                            ) : safeGet(user, 'isActive') ? (
                              <XCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                          </button> */}

                          {/* Edit Button */}
                          {canEditUsers && (
                            <button
                              onClick={() => handleEditClick(user)}
                              className="p-1 text-gray-400 hover:text-orange-600 rounded"
                              title="Edit user"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}

                          {/* Delete Button */}
                          {canDeleteUsers && (
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Delete user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}


                        </div>
                      </td>
                    </tr>
                  ))}                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {showAddModal && canCreateUsers && (
        <AddUserModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onUserAdded={handleUserAdded}
        />
      )}

      {showEditModal && selectedUser && canEditUsers && (
        <EditUserModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedUser(null)
          }}
          user={selectedUser}
          onUserUpdated={handleUserUpdated}
        />
      )}

      {showResetPasswordModal && selectedUser && canResetPasswords && (
        <ResetPasswordModal
          isOpen={showResetPasswordModal}
          onClose={() => {
            setShowResetPasswordModal(false)
            setSelectedUser(null)
          }}
          user={selectedUser}
          onPasswordReset={handlePasswordReset}
        />
      )}

      {showDeleteModal && selectedUser && canDeleteUsers && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedUser(null)
          }}
          onConfirm={async () => {
            try {
              await UserService.deleteUser(safeGet(selectedUser, '_id'))
              handleUserDeleted(safeGet(selectedUser, '_id'))
              setShowDeleteModal(false)
              setSelectedUser(null)
            } catch (error) {
              showToast(error?.message || 'Failed to delete user', 'error')
            }
          }}
          title="Delete User"
          message={`Are you sure you want to delete user "${safeGet(selectedUser, 'username')}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmVariant="destructive"
        />
      )}

      {/* Toast Notification */}
      {/* {toast && (
        <div className={`fixed top-4 right-4 z-50 max-w-md w-full ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white p-4 rounded-lg shadow-lg`}>
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )} */}

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setActionMenuOpen(null)}
        />
      )}
    </div>
  )
}